import { FC } from 'react';
import { FormControl, FormLabel, Input, Stack, useToast } from '@chakra-ui/react';
import { useAppDispatch, useAppSelector } from 'lib/redux';
import { selectMe, updateMe } from 'domains/account';
import { Formik, Form, Field, FieldProps } from 'formik';
import { ButtonSubmit } from 'components';
import { useBreakpointValue } from '@chakra-ui/media-query';
import { reformatKey, sha512, generateKeys } from 'lib/e2ee';
import { editPasswordSchema } from './editPasswordSchema';
import { loginClient } from 'lib/clients';

const initialValues = {
  currentPassword: '',
  password: ''
}

export const EditPasswordForm: FC = () => {
  const { user } = useAppSelector(selectMe);
  const isFullWidth = useBreakpointValue([ true, false ]);
  const dispatch = useAppDispatch();
  const toast = useToast();

  const onSubmit = async (values: any, { resetForm }: any) => {
    try {
      const u = { ...values };

      const password = u.password.trim();

      if (!password.length) {
        return;
      }

      if (user.has_password) {
        const currentPassword = u.currentPassword.trim();

        if (!currentPassword.length) {
          return;
        }

        // verify that the current password is correct
        const hash = sha512(currentPassword);
        
        try {
          await loginClient.postLogin(user.email, hash);
        }
        catch(e) {
          if (e?.response?.status !== 403) {
            throw e;
          }
  
          // This may be an older user, login using just the password.
          await loginClient.postLogin(user.email, currentPassword);
        }

        if (user.private_key_armored) {
          const privateKeyArmored = await reformatKey(user, currentPassword, password);
          u.private_key_armored = privateKeyArmored;
        }
      }

      if (!user.private_key_armored) {
        const userId = { email: user.email, name: `${user.first_name} ${user.last_name}`};
        const { privateKeyArmored, publicKeyArmored } = await generateKeys(userId, password);

        u.private_key_armored = privateKeyArmored;
        u.public_key_armored = publicKeyArmored
      }

      delete u.currentPassword;
      u.password = sha512(password);

      await dispatch(updateMe(u));
      toast({
        title: 'Profile updated',
        status: 'success',
        position: 'top-right',
        duration: 3000,
      });
      resetForm(initialValues)
    } catch (err) {
      if (err?.response?.status === 401) {
        return toast({
          title: 'Incorrect password',
          status: 'error',
          position: 'top-right',
          duration: 3000,
        });
      }

      console.error(err);

      toast({
        title: 'Password update failed',
        status: 'error',
        position: 'top-right',
        duration: 3000,
      });
    }
  };

  if (user.id.length === 0) {
    return null;
  }

  const schema = user.has_password ? editPasswordSchema : [editPasswordSchema[1]];

  return (
    <Formik enableReinitialize onSubmit={onSubmit} initialValues={initialValues}>
      <Form>
        <Stack spacing={6} className="form__stack">
          {schema.map((s) => (
            <Field key={s.name} name={s.name} className="form-field__wrapper">
              {({ field, form, meta }: FieldProps) => (
                <FormControl
                  isInvalid={Boolean(meta.touched && form.errors[field.name])}
                  isRequired={s?.isRequired || false}
                  isDisabled={s?.isDisabled || false}
                  className="form-field__control"
                >
                  <FormLabel className="form-profile__label">{s.label}</FormLabel>
                  <Input {...field} type={s?.type || 'text'} />
                </FormControl>
              )}
            </Field>
          ))}
          <ButtonSubmit isFullWidth={isFullWidth}>Update Password</ButtonSubmit>
        </Stack>
      </Form>
    </Formik>
  )
};